import React, { useEffect, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import { COMPLETE, SAVING } from '../hooks/useJobActions';
import useJobForm from '../hooks/useJobForm';
import Form from '../../form/Form';
import FormTextField from '../../form/FormTextField';
import FormUpload from '../../form/FormUpload';
import { defaultJobApplicantValues } from '../assets/schemas/add/addSchema';
import model from '../assets/models/applicantModel';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    paddingBottom: 0,
  },
  dialogContent: {
    minHeight: theme.typography.pxToRem(300),
  },
  dialogActions: {
    margin: theme.spacing(2),
  },
}));

const ApplicantDialog = ({
  onComplete,
  onClose,
  open,
  job,
  disabled,
  selectedApplicant,
}) => {
  const classes = useStyles();
  const [title, setTitle] = useState('');
  const { methods, actions } = useJobForm(model, defaultJobApplicantValues);
  const { saveApplicant, updateApplicant, status } = actions;
  const { reset, setValue } = methods;

  const { id, lastUpdate, applicants = [] } = job;
  const { name, company, attachment } = selectedApplicant;

  const localOnClose = useCallback(() => {
    reset(defaultJobApplicantValues);
    setTitle('');
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (status === COMPLETE) {
      onComplete();
      localOnClose();
    }
  }, [localOnClose, onComplete, status, id, open]);

  const cancel = useCallback(() => {
    localOnClose();
  }, [localOnClose]);

  useEffect(() => {
    if (open) {
      if (name) {
        reset(selectedApplicant);
        setValue('existingAttachment', attachment);
        setTitle(`${name} (${company})`);
      } else {
        setTitle('Add Applicant');
      }
      setValue('id', id);
      setValue('lastUpdate', lastUpdate);
      setValue('applicants', applicants);
    }
  }, [
    open,
    setValue,
    id,
    lastUpdate,
    applicants,
    company,
    name,
    reset,
    selectedApplicant,
    attachment,
  ]);

  const initialFiles = useMemo(() => {
    if (attachment) {
      // create empty file to show existing file name
      const existingAttachment = new File([''], attachment.attachmentName);
      return [existingAttachment];
    }
    return [];
  }, [attachment]);

  return (
    <Dialog open={open} onClose={localOnClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <b>{title}</b>
      </DialogTitle>
      <Form methods={methods}>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid container item xs={6} spacing={1}>
              <Grid item xs={12}>
                <FormTextField
                  autoFocus
                  id={model.name.id}
                  label={model.name.name}
                  disabled={disabled}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  id={model.company.id}
                  label={model.company.name}
                  disabled={disabled}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  id={model.comment.id}
                  label={model.comment.name}
                  disabled={disabled}
                  minRows={5}
                  variant="outlined"
                  multiline
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container item xs={6} spacing={1}>
              <Grid item xs={12}>
                <FormUpload
                  id={model.attachment.id}
                  initialFiles={initialFiles}
                  disabled={disabled}
                  acceptedFiles={[
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  ]}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={cancel}>Cancel</Button>
          {!disabled && (
            <Button
              onClick={name ? updateApplicant : saveApplicant}
              color="primary"
              variant="contained"
              disabled={status === SAVING}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};

ApplicantDialog.defaultProps = {
  job: {},
  selectedApplicant: {},
  disabled: false,
};

ApplicantDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  job: PropTypes.object,
  selectedApplicant: PropTypes.object,
  disabled: PropTypes.bool,
};

export default ApplicantDialog;
